import React, { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import { Loader } from 'components/atoms/Loader';
import { Box } from '@mui/material';
import { ERouteLinks } from 'models/route';
import { Table } from 'components/molecules/Table';
import { Pagination } from 'components/molecules/Pagination';
import { EPerPages, ISortOptions, mapToEPerPages } from 'models/table';
import { objectToQueryString, prepareQueryValues } from 'helpers/searchQuery';
import { joinValuesToString } from 'helpers/joinValuesToString';
import { replaceUrlIfOnPage } from 'utils/history';
import { useCase } from './hooks';
import { casesTableColumns, mapCasesToDataSource } from './helpers';
import {
  CaseSearchForm,
  ISearchFormValues,
  initialApplicationsSearchFormValues,
} from './CaseSearchForm';
import { useDictionary } from 'models/dictionaryContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { CardList } from 'components/molecules/CardList';
import { CasesCard } from './CasesCard';
import AssignCaseForm from './CaseDetailsPage/AssignCaseForm';
import { useAccount } from 'pages/AccountPage/hooks';
import { usePrivilegesValidation } from 'utils/privilegesValidations';

const Cases = () => {
  const { t } = useTranslations();
  const { isDesktop } = useThemeBreakpoint();
  const navigate = useNavigate();
  const { shortDateFormat, getProperties } = useGlobalProperty();
  const { clearAlert, samePageAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const initialFormValues = useMemo(() => {
    const queryValues = prepareQueryValues(
      initialApplicationsSearchFormValues,
      new URLSearchParams(searchParams),
    );
    return queryValues ? { ...initialApplicationsSearchFormValues, ...queryValues } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentPage, setCurrentPage] = useState<number>(() =>
    Number.parseInt(searchParams.get('currentPage') || '1', 10),
  );
  const [perPage, setPerPage] = useState<EPerPages>(() =>
    mapToEPerPages(searchParams.get('perPage') || EPerPages.perPage25),
  );
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [searchFormData, setSearchFormData] = useState<ISearchFormValues | undefined>(
    () => initialFormValues ?? undefined,
  );

  const CASES_TAB_QUERY = 'applications';

  const buildCasesQueryPath = (
    values: ISearchFormValues | undefined,
    page: number,
    size: EPerPages,
  ): string => {
    const queryString = objectToQueryString({
      ...(values ?? {}),
      assignedUsersUniqueIds: joinValuesToString(values?.assignedUsersUniqueIds),
      caseStatuses: joinValuesToString(values?.caseStatuses),
      processTypes: joinValuesToString(values?.processTypes),
      licenceAuthorities: joinValuesToString(values?.licenceAuthorities),
      regions: joinValuesToString(values?.regions),
      perPage: size,
      currentPage: page,
    });
    return `${pathname}?expand=${CASES_TAB_QUERY}&${queryString}`;
  };
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [assignCaseId, setAssignCaseId] = useState<string | null>(null);
  const { cases, paginator, getCasesData } = useCase();
  const { canAssignCase } = usePrivilegesValidation();
  const {
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
    caseProcessTypes,
    getCaseProcessTypes,
    weaponLicenceCaseStatus,
    getWeaponLicenceCaseStatus,
    region,
    getRegion,
  } = useDictionary();

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    if (!weaponLicencingAuthority) {
      getWeaponLicencingAuthority();
    }
    if (!caseProcessTypes) {
      getCaseProcessTypes();
    }
    if (!weaponLicenceCaseStatus) {
      getWeaponLicenceCaseStatus();
    }
    if (!region) {
      getRegion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    replaceUrlIfOnPage(buildCasesQueryPath(searchFormData, currentPage, perPage));
    (async () => {
      setIsPaginationLoading(true);
      if (!cases.length) {
        setIsLoading(true);
      }
      await getCasesData(currentPage, perPage, searchFormData, sort);
      setIsLoading(false);
      setIsPaginationLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, sort, searchFormData]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleShowDetails = (id: string) => {
    navigate(`${ERouteLinks.CasesDetailsPage.replace(':caseId', id)}`);
  };

  const handleAssign = (id: string) => setAssignCaseId(id);
  const updateData = async () =>
    await getCasesData(currentPage, perPage, searchFormData, sort);

  return (
    <>
      <CaseSearchForm
        onSubmit={(arg) => {
          setSearchFormData(arg);
          setCurrentPage(1);
        }}
        initialFormValues={initialFormValues ?? undefined}
        weaponLicencingAuthority={weaponLicencingAuthority}
        caseProcessTypes={caseProcessTypes}
        weaponLicenceCaseStatus={weaponLicenceCaseStatus}
        region={region}
      />

      {assignCaseId && (
        <AssignCaseForm
          isOpen={!!assignCaseId}
          close={() => setAssignCaseId(null)}
          updateData={updateData}
          caseId={assignCaseId}
          currentWorkerId={
            cases.find((item) => item.uniqueId === assignCaseId)?.assignedToUniqueId ??
            undefined
          }
        />
      )}

      {samePageAlert && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}

      {isLoading ? (
        <Loader isLoading={true} />
      ) : (
        <>
          {isDesktop ? (
            <Table
              columns={casesTableColumns({
                handleShowDetails,
                handleAssign,
                canAssignCase,
                t,
              })}
              dataSource={mapCasesToDataSource(
                cases,
                shortDateFormat,
                caseProcessTypes,
                weaponLicencingAuthority,
                weaponLicenceCaseStatus,
              )}
              onSort={setSort}
              id="cases_page_list-table"
            />
          ) : (
            <CardList
              items={mapCasesToDataSource(
                cases,
                shortDateFormat,
                caseProcessTypes,
                weaponLicencingAuthority,
                weaponLicenceCaseStatus,
              )}
              render={(data, index) => (
                <CasesCard
                  data={data}
                  key={index}
                  handleView={handleShowDetails}
                  handleAssign={handleAssign}
                  canAssignCase={canAssignCase}
                />
              )}
            />
          )}
        </>
      )}

      {paginator && (
        <Pagination
          count={paginator.totalElements}
          onChangePage={(page) => setCurrentPage(page)}
          current={currentPage}
          perPage={perPage}
          onChangePerPage={(value) => setPerPage(value)}
          isLoading={isPaginationLoading}
        />
      )}
    </>
  );
};

export default Cases;
